import React from 'react';
import {
    Button,
    Collapse,
    FormGroup,
} from 'reactstrap';
import { faPhone, faHistory } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { patterns } from '@s20/common/util/validation';
import { httpClient } from '@s20/services/httpClient';
import {
    FormCheckbox,
    toasty,
} from '../../common/forms/FormElements';
import { Textarea } from '../../common/forms/Textarea';
import Input from '../../common/forms/Input';
import ValidatedSelect from '../../common/forms/ValidatedSelect';
import { BaseFormViewModel } from '../../common/ViewModel';
import { handleFormSaveError } from '../../common/forms/ValidationError';
import CommonContext, { ApiRoutes, ApplicationPermissions } from '../../Common';
import { DateTimeInputFormat } from '../../dispatch/Dispatch';
import OrganizationContactForm from '../../organization/OrganizationContactForm';
import { OrganizationContact } from '../../organization/Organization';
import SlideForm from '../../common/forms/SlideForm';
import { CallLogModel } from './CallLog';
import CallLogHistoryForm from './CallLogHistoryForm';

// Contact Category IDs
const ContactCategory = {
    Client: 1,
    Employee: 2,
    Unassociated: 3,
};

// Call Direction IDs
const CallDirection = {
    Inbound: 1,
    Outbound: 2,
};

export default class CallLogForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);

        this.formRef = React.createRef();
        this.contactFormRef = React.createRef();

        this.state = {
            callLog: new CallLogModel(),
            show: false,
            calllogId: null,
            showCallLogHistory: false,
            interactionTypes: [],
            directions: [],
            dispositions: [],
            contactCategories: [],
            dispatchOffices: [],
            customers: [],
            contracts: [],
            jobs: [],
            contacts: [],
            employees: [],
            followUpEmployees: [],
            selectedContact: null,
            showContactsForm: false,
            contactFormIsReadOnly: false,
            dataLoading: false,
            ...new BaseFormViewModel(),
        };
    }

    onChange = (e) => {
        const {
            name, value, type, checked,
        } = e.target;
        const fieldValue = type === 'checkbox' ? checked : value;

        this.setState((state) => ({
            callLog: {
                ...state.callLog,
                [name]: fieldValue,
            },
        }));
    };

    onSelectChange = (selection, meta) => {
        const value = selection ? selection.value : null;

        this.setState((state) => ({
            callLog: {
                ...state.callLog,
                [meta.name]: value,
            },
        }));
    };

    componentDidMount() {
        return this.populateState();
    }

    async populateState() {
        const [interactionTypes, directions, dispositions, contactCategories, dispatchOffices] =
            await Promise.all([
                httpClient.js(ApiRoutes.typeAheads.callLogInteractionTypes()),
                httpClient.js(ApiRoutes.typeAheads.callDirections()),
                httpClient.js(ApiRoutes.typeAheads.callDispositions()),
                httpClient.js(ApiRoutes.typeAheads.contactCategories()),
                httpClient.js(ApiRoutes.typeAheads.selectableDispatchLocations()),
            ]);

        this.setState({
            interactionTypes,
            directions,
            dispositions,
            contactCategories,
            dispatchOffices,
        });
    }

    open = async (id) => {
        this.setState({ show: true, loading: true, calllogId: id });

        const callLog = await httpClient.js(ApiRoutes.callLog.byId(id));

        if (callLog.callDateTime) {
            callLog.callDateTime = DateTimeInputFormat(
                new Date(callLog.callDateTime),
            );
        }

        let customers = [];
        let contracts = [];
        let jobs = [];
        let contacts = [];
        let employees = [];
        let followUpEmployees = [];
        let { dispatchOffices } = this.state;

        if (callLog.dispatchOfficeId) {
            [dispatchOffices, customers, employees, followUpEmployees] = await Promise.all([
                httpClient.js(
                    ApiRoutes.typeAheads.selectableDispatchLocations(
                        callLog.dispatchOfficeId,
                    ),
                ),
                httpClient.js(
                    ApiRoutes.typeAheads.customersByDispatchLocation(
                        callLog.dispatchOfficeId,
                    ),
                ),
                httpClient.js(
                    ApiRoutes.callLog.employeesByDispatchLocation(
                        callLog.dispatchOfficeId,
                    ),
                ),
                httpClient.js(
                    ApiRoutes.callLog.followUpEmployeesByDispatchLocation(
                        callLog.dispatchOfficeId,
                    ),
                ),
            ]);
        }

        if (callLog.customerCompanyId) {
            let contactsRaw;
            [contracts, contactsRaw] = await Promise.all([
                httpClient.js(
                    ApiRoutes.typeAheads.contractsForCustomer(
                        callLog.customerCompanyId,
                        callLog.dispatchOfficeId,
                    ),
                ),
                httpClient.js(
                    ApiRoutes.company.contactDetails(callLog.customerCompanyId),
                ),
            ]);
            contacts = (contactsRaw ?? []).map((c) => ({
                ...c,
                value: c.id,
                label: c.fullName,
            }));
        }

        if (callLog.contractId) {
            jobs = await httpClient.js(
                ApiRoutes.callLog.jobsByContract(callLog.contractId),
            );
        }

        this.setState({
            callLog,
            customers,
            contracts,
            jobs,
            contacts,
            employees,
            followUpEmployees,
            dispatchOffices,
            loading: false,
        });
    };

    openNew = async () => {
        this.setState({ show: true, loading: true, calllogId: 'new' });
        const { user } = this.context;
        const userDispatchCompanyId = user?.dispatchCompanyId;

        const callLog = new CallLogModel();
        callLog.callDateTime = DateTimeInputFormat(new Date());
        callLog.callLogInteractionTypeId = 1; // Call
        callLog.callDirectionId = CallDirection.Inbound;
        callLog.contactCategoryId = ContactCategory.Client;
        callLog.dispatchOfficeId = userDispatchCompanyId || null;

        let customers = [];
        let employees = [];
        let followUpEmployees = [];

        if (userDispatchCompanyId) {
            [customers, employees, followUpEmployees] = await Promise.all([
                httpClient.js(
                    ApiRoutes.typeAheads.customersByDispatchLocation(
                        userDispatchCompanyId,
                    ),
                ),
                httpClient.js(
                    ApiRoutes.callLog.employeesByDispatchLocation(
                        userDispatchCompanyId,
                    ),
                ),
                httpClient.js(
                    ApiRoutes.callLog.followUpEmployeesByDispatchLocation(
                        userDispatchCompanyId,
                    ),
                ),
            ]);
        }

        this.setState({
            callLog,
            loading: false,
            customers,
            employees,
            followUpEmployees,
            contracts: [],
            jobs: [],
            contacts: [],
        });
    };

    onClose = () => {
        this.setState({
            show: false,
            callLog: new CallLogModel(),
            calllogId: null,
            errors: {},
            validationMessage: '',
        });
    };

    onSubmit = async () => {
        const { callLog } = this.state;

        this.setState({ saving: true });

        const callLogToSave = {
            ...callLog,
            callDateTime: callLog.callDateTime
                ? new Date(callLog.callDateTime).toISOString()
                : callLog.callDateTime,
        };

        let saved = false;

        if (callLogToSave.id > 0) {
            saved = await this.update(callLogToSave);
        } else {
            saved = await this.create(callLogToSave);
        }

        this.setState({ saving: false });

        if (saved) {
            this.setState({
                show: false,
                callLog: new CallLogModel(),
                errors: {},
                validationMessage: '',
            });
            if (this.props.onSaveCallback) {
                this.props.onSaveCallback();
            }
        }
    };

    create = async (callLog) => {
        const response = await httpClient
            .post(ApiRoutes.callLog.create(), callLog)
            .catch(this.handleSaveError);

        if (response) {
            toasty.success('Call Log Created', 'The call log has been saved.');
            return true;
        }

        return false;
    };

    update = async (callLog) => {
        const response = await httpClient
            .put(ApiRoutes.callLog.update(callLog.id), callLog)
            .catch(this.handleSaveError);

        if (response) {
            toasty.success('Call Log Updated', 'The call log has been saved.');
            return true;
        }

        return false;
    };

    handleSaveError = (err) => handleFormSaveError(this, err);

    onDispatchOfficeChanged = async (selection) => {
        const dispatchOfficeId = selection ? selection.value : null;

        let customers = [];
        let employees = [];
        let followUpEmployees = [];

        if (dispatchOfficeId) {
            this.setState({ dataLoading: true });
            try {
                [customers, employees, followUpEmployees] = await Promise.all([
                    httpClient.js(
                        ApiRoutes.typeAheads.customersByDispatchLocation(
                            dispatchOfficeId,
                        ),
                    ),
                    httpClient.js(
                        ApiRoutes.callLog.employeesByDispatchLocation(
                            dispatchOfficeId,
                        ),
                    ),
                    httpClient.js(
                        ApiRoutes.callLog.followUpEmployeesByDispatchLocation(
                            dispatchOfficeId,
                        ),
                    ),
                ]);
            } finally {
                this.setState({ dataLoading: false });
            }
        }

        this.setState((state) => ({
            customers,
            employees,
            followUpEmployees,
            contracts: [],
            jobs: [],
            contacts: [],
            callLog: {
                ...state.callLog,
                dispatchOfficeId,
                customerCompanyId: null,
                contractId: null,
                jobId: null,
                customerContactId: null,
                employeeId: null,
                contactFirstName: '',
                contactLastName: '',
                contactPhoneNumber: '',
                contactEmail: '',
                followUpOwnerId: null,
            },
        }));
    };

    onContactCategoryChanged = (selection) => {
        const contactCategoryId = selection ? selection.value : null;

        this.setState((state) => ({
            contracts: [],
            jobs: [],
            contacts: [],
            callLog: {
                ...state.callLog,
                contactCategoryId,
                customerCompanyId: null,
                contractId: null,
                jobId: null,
                customerContactId: null,
                employeeId: null,
                contactFirstName: '',
                contactLastName: '',
                contactPhoneNumber: '',
                contactEmail: '',
            },
        }));
    };

    onCustomerChanged = async (selection) => {
        const customerCompanyId = selection ? selection.value : null;

        let contracts = [];
        let contacts = [];

        if (customerCompanyId) {
            const { callLog } = this.state;
            let contactsRaw;
            this.setState({ dataLoading: true });
            try {
                [contracts, contactsRaw] = await Promise.all([
                    httpClient.js(
                        ApiRoutes.typeAheads.contractsForCustomer(
                            customerCompanyId,
                            callLog.dispatchOfficeId,
                        ),
                    ),
                    httpClient.js(
                        ApiRoutes.company.contactDetails(customerCompanyId),
                    ),
                ]);
            } finally {
                this.setState({ dataLoading: false });
            }
            contacts = (contactsRaw ?? []).map((c) => ({
                ...c,
                value: c.id,
                label: c.fullName,
            }));
        }

        this.setState((state) => ({
            contracts,
            contacts,
            jobs: [],
            callLog: {
                ...state.callLog,
                customerCompanyId,
                contractId: null,
                jobId: null,
                customerContactId: null,
                contactFirstName: '',
                contactLastName: '',
                contactPhoneNumber: '',
                contactEmail: '',
            },
        }));
    };

    onContractChanged = async (selection) => {
        const contractId = selection ? selection.value : null;

        let jobs = [];

        if (contractId) {
            this.setState({ dataLoading: true });
            try {
                jobs = await httpClient.js(
                    ApiRoutes.callLog.jobsByContract(contractId),
                );
            } finally {
                this.setState({ dataLoading: false });
            }
        }

        this.setState((state) => ({
            jobs,
            callLog: {
                ...state.callLog,
                contractId,
                jobId: null,
            },
        }));
    };

    onContactChanged = (selection) => {
        this.setState((state) => ({
            callLog: {
                ...state.callLog,
                customerContactId: selection?.value ?? null,
                contactFirstName: selection?.firstName ?? '',
                contactLastName: selection?.lastName ?? '',
                contactPhoneNumber: selection?.mobilePhone || selection?.officePhone || '',
                contactEmail: selection?.email ?? '',
            },
        }));
    };

    onEmployeeChanged = (selection) => {
        this.setState((state) => ({
            callLog: {
                ...state.callLog,
                employeeId: selection?.value ?? null,
                contactFirstName: selection?.firstName ?? '',
                contactLastName: selection?.lastName ?? '',
                contactPhoneNumber: selection?.mobilePhone || selection?.officePhone || '',
                contactEmail: selection?.email ?? '',
            },
        }));
    };

    onFollowUpChanged = (e) => {
        const { checked } = e.target;

        this.setState((state) => ({
            callLog: {
                ...state.callLog,
                followUpRequested: checked,
                ...(!checked && {
                    followUpInstructions: '',
                    followUpOwnerId: null,
                }),
            },
        }));
    };

    onViewHistory = () => {
        this.setState({ showCallLogHistory: true });
    };

    onCloseCallLogHistory = () => {
        this.setState({ showCallLogHistory: false });
    };

    onAddContact = () => {
        const { callLog } = this.state;
        this.context.setFormOpened(true);
        this.contactFormRef.current.resetForm();
        this.setState({
            selectedContact: {
                ...new OrganizationContact(),
                id: null,
                companyId: callLog.customerCompanyId,
            },
            showContactsForm: true,
        });
    };

    onOrganizationContactSaved = async () => {
        this.setState({ contactFormIsReadOnly: true });

        const { selectedContact, callLog } = this.state;
        const isNew = parseInt(selectedContact.id ?? 0, 10) <= 0;
        const result = await httpClient.andGetResponse(
            isNew ? httpClient.types.post : httpClient.types.put,
            isNew
                ? ApiRoutes.locationContact.create()
                : ApiRoutes.locationContact.update(selectedContact.id),
            { ...selectedContact },
            'Error Saving Contact',
        );

        if (result) {
            toasty.success('Contact saved.');

            const newContactId = result.id ?? result;

            const contactsRaw = await httpClient.js(
                ApiRoutes.company.contactDetails(callLog.customerCompanyId),
            );
            const contacts = (contactsRaw ?? []).map((c) => ({
                ...c,
                value: c.id,
                label: c.fullName,
            }));

            this.setState((state) => ({
                contacts,
                callLog: {
                    ...state.callLog,
                    customerContactId: newContactId,
                    contactFirstName: selectedContact.firstName || '',
                    contactLastName: selectedContact.lastName || '',
                    contactPhoneNumber:
                        selectedContact.mobilePhone ||
                        selectedContact.officePhone ||
                        '',
                    contactEmail: selectedContact.email || '',
                },
                showContactsForm: false,
                contactFormIsReadOnly: false,
            }));
        } else {
            this.setState({ contactFormIsReadOnly: false });
        }
    };

    getFilteredDispositions = () => {
        const { dispositions, callLog } = this.state;

        if (!callLog || !callLog.callDirectionId) return dispositions;

        return dispositions.filter(
            (d) =>
                d.directionIds &&
                d.directionIds.includes(callLog.callDirectionId),
        );
    };

    getPhoneError = () => {
        const { callLog } = this.state;

        if (!callLog.contactPhoneNumber) {
            return '';
        }

        const phonePattern = new RegExp(patterns.htmlPhone);
        if (!phonePattern.test(callLog.contactPhoneNumber)) {
            return 'Please enter a valid phone number (ex: 555-555-5555)';
        }

        return '';
    };

    renderClientSection = (canEdit) => {
        const {
            callLog, customers, contracts, jobs, contacts,
        } = this.state;

        return (
            <>
                <ValidatedSelect
                    id="customerCompanyId"
                    name="customerCompanyId"
                    label="Customer"
                    options={customers}
                    getOptionLabel={(o) => o.label}
                    getOptionValue={(o) => o.value}
                    value={
                        customers.find(
                            (c) => c.value === callLog.customerCompanyId,
                        ) ?? ''
                    }
                    onChange={this.onCustomerChanged}
                    isDisabled={!canEdit || !callLog.dispatchOfficeId}
                    required
                    error="Customer is required."
                />

                <ValidatedSelect
                    id="contractId"
                    name="contractId"
                    label="Contract"
                    options={contracts}
                    getOptionLabel={(o) => o.label}
                    getOptionValue={(o) => o.value}
                    value={
                        contracts.find(
                            (c) => c.value === callLog.contractId,
                        ) ?? ''
                    }
                    onChange={this.onContractChanged}
                    isDisabled={!canEdit || !callLog.customerCompanyId}
                />

                <ValidatedSelect
                    id="jobId"
                    name="jobId"
                    label="Job"
                    options={jobs}
                    getOptionLabel={(o) => o.label}
                    getOptionValue={(o) => o.value}
                    value={
                        jobs.find((j) => j.value === callLog.jobId) ?? ''
                    }
                    onChange={this.onSelectChange}
                    isDisabled={!canEdit || !callLog.contractId}
                />

                <ValidatedSelect
                    id="customerContactId"
                    name="customerContactId"
                    label="Contact"
                    options={contacts}
                    getOptionLabel={(o) => o.label}
                    getOptionValue={(o) => o.value}
                    value={
                        contacts.find(
                            (c) => c.value === callLog.customerContactId,
                        ) ?? ''
                    }
                    onChange={this.onContactChanged}
                    isDisabled={!canEdit || !callLog.customerCompanyId}
                    required
                    error="Contact is required."
                    showAddButton={canEdit && callLog.customerCompanyId}
                    onAddButtonClick={() => this.onAddContact()}
                />
            </>
        );
    };

    renderEmployeeSection = (canEdit) => {
        const {
            callLog, employees, customers, contracts, jobs,
        } = this.state;

        return (
            <>
                <ValidatedSelect
                    id="employeeId"
                    name="employeeId"
                    label="Employee"
                    options={employees}
                    getOptionLabel={(o) => o.label}
                    getOptionValue={(o) => o.value}
                    value={
                        employees.find(
                            (e) => e.value === callLog.employeeId,
                        ) ?? ''
                    }
                    onChange={this.onEmployeeChanged}
                    isDisabled={!canEdit || !callLog.dispatchOfficeId}
                />

                <ValidatedSelect
                    id="customerCompanyId"
                    name="customerCompanyId"
                    label="Customer"
                    options={customers}
                    getOptionLabel={(o) => o.label}
                    getOptionValue={(o) => o.value}
                    value={
                        customers.find(
                            (c) => c.value === callLog.customerCompanyId,
                        ) ?? ''
                    }
                    onChange={this.onCustomerChanged}
                    isDisabled={!canEdit || !callLog.dispatchOfficeId}
                />

                <ValidatedSelect
                    id="contractId"
                    name="contractId"
                    label="Contract"
                    options={contracts}
                    getOptionLabel={(o) => o.label}
                    getOptionValue={(o) => o.value}
                    value={
                        contracts.find(
                            (c) => c.value === callLog.contractId,
                        ) ?? ''
                    }
                    onChange={this.onContractChanged}
                    isDisabled={!canEdit || !callLog.customerCompanyId}
                />

                <ValidatedSelect
                    id="jobId"
                    name="jobId"
                    label="Job"
                    options={jobs}
                    getOptionLabel={(o) => o.label}
                    getOptionValue={(o) => o.value}
                    value={
                        jobs.find((j) => j.value === callLog.jobId) ?? ''
                    }
                    onChange={this.onSelectChange}
                    isDisabled={!canEdit || !callLog.contractId}
                />
            </>
        );
    };

    renderUnassociatedSection = (canEdit) => {
        const { callLog } = this.state;

        return (
            <>
                <Input
                    label="First Name"
                    id="contactFirstName"
                    name="contactFirstName"
                    value={callLog.contactFirstName ?? ''}
                    onChange={this.onChange}
                    placeholder="First name"
                    maxLength="100"
                    disabled={!canEdit}
                />

                <Input
                    label="Last Name"
                    id="contactLastName"
                    name="contactLastName"
                    value={callLog.contactLastName ?? ''}
                    onChange={this.onChange}
                    placeholder="Last name"
                    maxLength="100"
                    disabled={!canEdit}
                />

                <Input
                    label="Phone Number"
                    id="contactPhoneNumber"
                    name="contactPhoneNumber"
                    value={callLog.contactPhoneNumber ?? ''}
                    onChange={this.onChange}
                    placeholder="ex: 555-555-5555"
                    type="tel"
                    pattern={patterns.htmlPhone}
                    title="Please enter a valid phone number (ex: 555-555-5555)"
                    autoComplete="off"
                    error={this.getPhoneError()}
                    disabled={!canEdit}
                />

                <Input
                    label="Email"
                    id="contactEmail"
                    name="contactEmail"
                    value={callLog.contactEmail ?? ''}
                    onChange={this.onChange}
                    placeholder="Email address"
                    maxLength="200"
                    disabled={!canEdit}
                />
            </>
        );
    };

    renderCategorySpecificSection = (canEdit) => {
        const { callLog } = this.state;

        switch (callLog.contactCategoryId) {
            case ContactCategory.Client:
                return this.renderClientSection(canEdit);
            case ContactCategory.Employee:
                return this.renderEmployeeSection(canEdit);
            case ContactCategory.Unassociated:
                return this.renderUnassociatedSection(canEdit);
            default:
                return null;
        }
    };

    render() {
        const {
            callLog,
            show,
            loading,
            calllogId,
            validationMessage,
            interactionTypes,
            directions,
            contactCategories,
            dispatchOffices,
            followUpEmployees,
            selectedContact,
            showContactsForm,
            contactFormIsReadOnly,
            saving,
            formValidated,
            showCallLogHistory,
        } = this.state;

        const isNew = calllogId === 'new';
        const { checkUserHasPermission } = this.context;
        const canEdit = isNew || checkUserHasPermission(ApplicationPermissions.callLog_edit);
        const filteredDispositions = this.getFilteredDispositions();
        const showCategorySection =
            callLog.callDirectionId &&
            callLog.dispatchOfficeId &&
            callLog.callDispositionId &&
            callLog.contactCategoryId;

        if (!show) {
            return;
        }

        return (
            <>
                <SlideForm
                    key={calllogId}
                    size="col-xl-12 col-md-12 col-xs-12"
                    fullScreen
                    loading={loading}
                    saving={saving}
                    show
                    onClose={this.onClose}
                    formIcon={faPhone}
                    formTitle={`${isNew ? 'New' : 'Edit'} Call Log`}
                    onSubmit={this.onSubmit}
                    errors={this.state.errors}
                    error={validationMessage}
                    setIsValidated={(value) => {
                        this.setState({ formValidated: value });
                    }}
                    isValidated={formValidated}
                    isValid={(value) => {
                        if (!value) {
                            toasty.error(
                                'Validation Failed. Please review and complete all required data',
                            );
                        }
                    }}
                >
                    <Input
                        label="Call Date/Time"
                        id="callDateTime"
                        name="callDateTime"
                        type="datetime-local"
                        value={callLog.callDateTime ?? ''}
                        onChange={this.onChange}
                        required
                        error="Call Date/Time is required."
                        disabled={!canEdit}
                    />

                    <ValidatedSelect
                        id="callLogInteractionTypeId"
                        name="callLogInteractionTypeId"
                        required
                        options={interactionTypes}
                        value={interactionTypes.find((t) => t.value === callLog.callLogInteractionTypeId) ?? ''}
                        onChange={this.onSelectChange}
                        error="Interaction Type is required."
                        label="Interaction Type"
                        isDisabled={!canEdit}
                    />

                    <ValidatedSelect
                        id="callDirectionId"
                        name="callDirectionId"
                        label="Direction"
                        required
                        options={directions}
                        value={directions.find((d) => d.value === callLog.callDirectionId) ?? ''}
                        onChange={this.onSelectChange}
                        error="Direction is required."
                        isDisabled={!canEdit}
                    />

                    <ValidatedSelect
                        id="dispatchOfficeId"
                        name="dispatchOfficeId"
                        label="Dispatch Office"
                        required
                        options={dispatchOffices}
                        value={dispatchOffices.find((d) => d.value === callLog.dispatchOfficeId) ?? ''}
                        onChange={this.onDispatchOfficeChanged}
                        error="Dispatch Office is required."
                        isDisabled={!canEdit}
                    />
                    <ValidatedSelect
                        id="callDispositionId"
                        name="callDispositionId"
                        label="Disposition"
                        required
                        options={filteredDispositions}
                        value={filteredDispositions.find((d) => d.value === callLog.callDispositionId) ?? ''}
                        onChange={this.onSelectChange}
                        error="Disposition is required."
                        isDisabled={!canEdit}
                    />

                    <ValidatedSelect
                        id="contactCategoryId"
                        name="contactCategoryId"
                        label="Contact Category"
                        required
                        options={contactCategories}
                        value={contactCategories.find((c) => c.value === callLog.contactCategoryId) ?? ''}
                        onChange={this.onContactCategoryChanged}
                        error="Contact category is required."
                        isDisabled={!canEdit}
                    />

                    {showCategorySection &&
                        this.renderCategorySpecificSection(canEdit)}

                    <Textarea
                        label="Call Notes"
                        id="callNotes"
                        name="callNotes"
                        value={callLog.callNotes ?? ''}
                        onChange={this.onChange}
                        placeholder="Enter call notes"
                        rows="4"
                        disabled={!canEdit}
                    />

                    <FormGroup>
                        <FormCheckbox
                            id="followUpRequested"
                            name="followUpRequested"
                            checked={
                                callLog.followUpRequested ?? false
                            }
                            onChange={this.onFollowUpChanged}
                            labelText="Follow-Up Requested"
                            disabled={!canEdit}
                        />
                    </FormGroup>

                    <Collapse isOpen={callLog.followUpRequested}>
                        <Textarea
                            label="Follow-Up Instructions"
                            id="followUpInstructions"
                            name="followUpInstructions"
                            value={
                                callLog.followUpInstructions ?? ''
                            }
                            onChange={this.onChange}
                            placeholder="Enter follow-up instructions"
                            rows="3"
                            required={callLog.followUpRequested}
                            error="Follow-up instructions are required when follow-up is requested."
                            disabled={!canEdit}
                        />

                        <ValidatedSelect
                            id="followUpOwnerId"
                            name="followUpOwnerId"
                            required={callLog.followUpRequested}
                            label="Follow-Up Owner"
                            options={followUpEmployees}
                            value={followUpEmployees.find((e) => e.value === callLog.followUpOwnerId) ?? ''}
                            onChange={this.onSelectChange}
                            isDisabled={!canEdit || !callLog.dispatchOfficeId}
                            error="Follow-up owner is required when follow-up is requested."
                            menuPlacement="top"
                        />
                    </Collapse>

                    {callLog.id > 0 && (
                        <>
                            <hr />
                            <div className="d-flex justify-content-center mb-2">
                                <Button
                                    color="link"
                                    className="p-0"
                                    onClick={this.onViewHistory}
                                >
                                    <FontAwesomeIcon
                                        icon={faHistory}
                                        className="mr-1"
                                    />
                                    View Edit History
                                </Button>
                            </div>
                        </>
                    )}
                    <div className="pb-5" />
                </SlideForm>

                <CallLogHistoryForm
                    show={showCallLogHistory}
                    callLogId={callLog?.id}
                    onClose={this.onCloseCallLogHistory}
                />

                <OrganizationContactForm
                    ref={this.contactFormRef}
                    show={showContactsForm}
                    readOnly={contactFormIsReadOnly}
                    allowContactTypeEdit
                    contact={selectedContact}
                    onClose={() => {
                        this.setState({ showContactsForm: false });
                    }}
                    onChange={(e) => {
                        const { selectedContact: sc } = this.state;
                        sc[e.target.name] = e.target.value;
                        this.setState({ selectedContact: { ...sc } });
                    }}
                    onCheckedChanged={(e) => {
                        const { selectedContact: sc } = this.state;
                        sc[e.target.name] = e.target.checked;
                        this.setState({ selectedContact: { ...sc } });
                    }}
                    onContactTypeChanged={(items) => {
                        const { selectedContact: sc } = this.state;
                        sc.contactTypes = items.map((x) => x.value);
                        this.setState({ selectedContact: { ...sc } });
                    }}
                    onSaveCallback={this.onOrganizationContactSaved}
                />
            </>
        );
    }
}
